// Canlı Durum "Bu hafta" şeridi (görev #195 Vegas / #196 Prime, 2026-10-09) — Pazartesi 00:00
// İstanbul'dan şu ana kadar marka toplamları. Kaynak İKİNCİ bir sayım DEĞİL: `GET /api/weekly-report
// ?week=current`'in aynı yanıtı (aynı L1/L2 cache, aynı tarama) → her sayı Haftalık Rapor'un o hafta
// gösterdiğiyle birebir aynı:
//   - Güncel yatırım = MoneyStrip "Yatırım (24s/48s)" (`summary.deposit12h/36h`, ham). Cari haftada
//     son görüşmelerin pencereleri henüz kapanmadığı için BÜYÜR — "güncel" olan budur.
//   - Toplam çağrı   = MoneyStrip "Toplam Çağrı" (Arama Yapılmadı HARİÇ) — çağıran hesaplar
//     (WeeklyReport.tsx weeklyRealCalls; sonuç sütunları oradaki RESULTS'tan türer).
//   - SMS            = markanın TÜM gönderilen SMS'i (`sms.sent`; karnede satırı olmayan dahil).
//   - WhatsApp-TG    = `messaging.total`.
//   - QMS ortalaması = sunucunun marka ortalaması (değerlendirme satırları üzerinden, ajan
//     ortalamalarının ortalaması DEĞİL); kritik hata = markanın toplamı — karne alt satırından (yalnız görüşmesi
//     olan / hariç tutulmayan satırlar) BÜYÜK olabilir: görüşmesiz ya da hariç bir isme düşen kritik burada sayılır.
//   - Başarı ort.    = karnenin "Ekip toplamı" BAŞARI ORANI (ajan oranlarının ağırlıksız ortalaması) — ama cari
//     haftada henüz VADESİ GELMEMİŞ (boş) hücreler paydadan düşülür: rapor onları "yatırım yok" sayar, şerit
//     Pazartesi öğlen %0 gösteriyordu. Bekleyen hücre yoksa (kapanmış hafta) rapordaki sayının KENDİSİ.
// Eksik değer `null` → hücre BOŞ (§6): SMS/QMS okunamadıysa 0 basılmaz (0 bir cevaptır); görüşmesi
// olan hiç temsilci yoksa başarı ortalamasının paydası yoktur → motorun `meanOf([])=0`'ı BOŞ olur.
//
// SAF modül (React yok) — test/week-kpis.test.mjs esbuild ile yükleyip pinler.

export interface WeekKpis {
  deposit24h: number;
  deposit48h: number;
  calls: number;
  /** `null` = SMS bu yanıtta okunamadı (BOŞ basılır, 0 değil). */
  smsSent: number | null;
  smsFailed: number | null;
  messages: number;
  messagesAnswerRatePct: number | null;
  /** `null` = QMS kapalı/okunamadı ya da bu hafta değerlendirme yok. */
  qmsAvg: number | null;
  qmsEvalCount: number | null;
  criticals: number | null;
  /** `null` = bu hafta görüşmesi olan temsilci yok (oranın paydası yok). */
  successRate24hPct: number | null;
  successRate48hPct: number | null;
}

/** Şeridin ihtiyaç duyduğu alt küme — WeeklyBrandReport'u bu modüle bağımlı kılmamak için yapısal. */
export interface WeekKpiReport {
  summary: { deposit12h: number; deposit36h: number };
  agents: {
    conversations: number; successBase?: number;
    deposited12h?: number; deposited36h?: number; pendingCells12h?: number; pendingCells36h?: number;
  }[];
  teamAverage: { successRate12hPct: number; successRate36hPct: number } | null;
  messaging: { total: number; answerRatePct: number } | null;
  qms?: { available: true; evalCount: number; avgScore: number | null; criticalCount: number } | { available: false };
  sms?: { available: true; sent: number; failed: number } | { available: false };
}

/** The team rate over DECIDED cells: each agent's deposited / (conversations − still-empty cells), unweighted mean, one
 *  decimal (the engine's meanOf/pct). No pending cell anywhere → the report's own figure (a settled week matches it
 *  exactly); every cell still pending → null (nothing decided yet — blank, not %0). An older server sends no pending
 *  counts → the report's figure, as before. */
function decidedTeamRate(agents: WeekKpiReport['agents'], h: '12h' | '36h', reportRate: number): number | null {
  const pend = (a: WeekKpiReport['agents'][number]) => (h === '12h' ? a.pendingCells12h : a.pendingCells36h) || 0;
  if (!agents.some((a) => pend(a) > 0)) return reportRate;
  const rates: number[] = [];
  for (const a of agents) {
    const won = h === '12h' ? a.deposited12h : a.deposited36h;
    // Over the success base (görüşme − DAVET, #297); an older server sends none → conversations, as its rate did.
    const decided = (typeof a.successBase === 'number' ? a.successBase : a.conversations || 0) - pend(a);
    if (typeof won !== 'number') return reportRate;   // a shape without counts: keep the report's figure
    if (decided > 0) rates.push(Math.round((1000 * won) / decided) / 10);
  }
  if (!rates.length) return null;
  return Math.round((10 * rates.reduce((x, y) => x + y, 0)) / rates.length) / 10;
}

/** The weekly engine's ROUTINE caveat for the current week ("N yatırım hücresi HENÜZ BOŞ …"): present on almost every
 *  live day, so it must not light the strip's "don't trust these numbers" icon — a stale-fallback or missing-tab caveat
 *  would look exactly like a normal day. Twin of the engine text (src/engine/weekly-report.js). */
export const isRoutinePendingCaveat = (c: string): boolean => /yatırım hücresi HENÜZ BOŞ/.test(c);

export function weekKpis(report: WeekKpiReport, realCalls: number): WeekKpis {
  const qms = report.qms && report.qms.available ? report.qms : null;
  const sms = report.sms && report.sms.available ? report.sms : null;
  const hasConversations = Array.isArray(report.agents) && report.agents.some((a) => (a.conversations || 0) > 0);
  const team = hasConversations ? report.teamAverage : null;
  return {
    deposit24h: report.summary.deposit12h,
    deposit48h: report.summary.deposit36h,
    calls: realCalls,
    smsSent: sms ? sms.sent : null,
    smsFailed: sms ? sms.failed : null,
    messages: report.messaging ? report.messaging.total : 0,
    // Mesajsız haftada cevaplama oranının paydası yok → BOŞ, %0,0 değil (§6).
    messagesAnswerRatePct: report.messaging && report.messaging.total > 0 ? report.messaging.answerRatePct : null,
    qmsAvg: qms ? qms.avgScore : null,
    qmsEvalCount: qms ? qms.evalCount : null,
    criticals: qms ? qms.criticalCount : null,
    successRate24hPct: team ? decidedTeamRate(report.agents, '12h', team.successRate12hPct) : null,
    successRate48hPct: team ? decidedTeamRate(report.agents, '36h', team.successRate36hPct) : null,
  };
}
